import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { type Board, DX, DY, headDir, occupancy, ray } from "./board";
import { TILE_BASE, TILE_MOTIF, motifPath, ringOf, tileKind, tilePath } from "./tiles";

/** رنگِ هر بازیکن روی تخته‌ی مشترک: طلاییِ خودم، بعد آبی، بنفش و سبز — از هم دور، حتی برای رنگ‌کوری. */
export const PLAYER_COLORS = ["#c99613", "#2563eb", "#9333ea", "#0f9f8a"] as const;

/**
 * تخته‌ی فلش‌ها روی بومِ بی‌انتها: سفید با نقطه‌های کم‌رنگ تا هر جا که بروی. با یک انگشت (یا موش) کشیده
 * می‌شود، با دو انگشت، چرخِ موش یا دکمه‌ها بزرگ و کوچک می‌شود، و «جا کن» دوباره همه‌اش را در صفحه می‌نشاند.
 *
 * لمسِ فلش هنگامِ برداشتنِ انگشت حساب می‌شود و فقط اگر انگشت جابه‌جا نشده باشد — وگرنه هر کشیدنی یک
 * فلش را هم می‌زد. فلشِ زیرِ انگشت از همان لحظه‌ی گذاشتن پررنگ می‌شود تا لمس بی‌پاسخ نماند.
 *
 * هر فلش یک مسیر است از مرکزِ دُم تا مرکزِ نوک، و بعد راست تا بیرونِ تخته. فقط تکه‌ای به درازای خودِ
 * فلش دیده می‌شود (stroke-dasharray)، و جابه‌جاکردنِ همان تکه روی مسیر (stroke-dashoffset) فلش را
 * مثلِ مار از پیچ‌هایش بیرون می‌کشد: نوک صاف می‌رود و تن دنبالش از همان پیچ‌ها رد می‌شود.
 *
 * دوربین و انیمیشن‌ها مستقیم روی DOM اند (requestAnimationFrame)، نه با رندرِ React در هر فریم.
 *
 * حسِ بازی (کاربر، ۹ آبان: «دسته‌ی ۱»):
 * - جای هر فلشِ رفته کاشی می‌شود (tiles.ts) و تخته‌ی تمام‌شده نقشِ کاشی‌کاری‌شده است؛ با done موجی از مرکز رویش می‌دود.
 * - پنج فلشِ آخر خودشان را نشان می‌دهند: می‌تپند، حلقه‌ای به اندازه‌ی ثابتِ صفحه دورِ نوکشان می‌پرد، و اگر بیرونِ
 *   صفحه یا ریزند دوربین یک بار سراغشان می‌رود — روی نقشه‌ی هزارفلشی، فلشِ آخر را پیدا کردن خودش یک بازی بود.
 */

export interface BoardHandle {
  /** فلش بیرون می‌رود؛ dist = خانه‌های سرِ راه تا لبه. */
  fly(id: number, dist: number): void;
  /** فلش تا مانع می‌رود و برمی‌گردد؛ dist = چند خانه تا مانع. */
  bump(id: number, dist: number): void;
  /** بزرگ‌نمایی گرداگردِ وسطِ صفحه. */
  zoomBy(f: number): void;
  /** همه‌ی تخته در صفحه. */
  fit(): void;
}

interface Props {
  board: Board;
  /** فلش‌های زنده؛ فلشی که در حالِ پرواز است تا پایانِ پروازش کشیده می‌شود. */
  alive: readonly boolean[];
  onTap?: (id: number) => void;
  /** لمسِ فلش نمی‌گیرد (شمارش، یخ‌زدن، میانِ دو تخته)؛ کشیدن و بزرگ‌نمایی می‌ماند. */
  locked?: boolean;
  /** false = فقط نمایش (تخته‌ی نمونه‌ی منو): نه لمس، نه کشیدن. */
  interactive?: boolean;
  /** فضای خالیِ رویِ بوم که زیرِ HUD است و تخته نباید زیرش بنشیند (پیکسل). */
  inset?: { top: number; bottom: number };
  /** تخته‌ی مشترک: فقط فلش‌های این صاحب لمس می‌شوند و پررنگ‌اند؛ بقیه کم‌رنگ. */
  own?: number;
  /** تیمی: هم‌تیمی‌ها — فلش‌هایشان پیداست (لای فلش‌های تو) ولی لمس نمی‌شود. */
  mates?: readonly number[];
  /** تخته‌ی مشترک: دوربین اول کلِ شکل را نشان می‌دهد و بعد روی این چهارگوش (سهمِ من) می‌نشیند؛ «جا کن» هم به همین. */
  focus?: { x: number; y: number; w: number; h: number };
  /**
   * همکاری: تکه‌های نقشه‌ی بزرگ — بسته (مه و قفل)، باز (قابِ نرم) یا تمام‌شده (قابِ طلایی و تیک). فلش‌های تکه‌ی
   * بسته زنده نیستند و کشیده نمی‌شوند.
   */
  parts?: readonly { x: number; y: number; w: number; h: number; state: "locked" | "open" | "done" }[];
  /** همکاری: هر بار که این چهارگوش عوض شود (تکه‌ی تازه باز شد)، دوربین آرام رویش می‌رود. */
  follow?: { x: number; y: number; w: number; h: number };
  /**
   * نقشه‌ی بزرگ: اگر هر خانه کمتر از این‌قدر پیکسل است، لمس فلش نمی‌زند و دوربین را همان‌جا تا خانه‌های لمس‌شدنی نزدیک
   * می‌کند — روی هزار فلش در صفحه‌ی گوشی، لمسِ فلشِ سه‌پیکسلی فقط قلب می‌سوزاند.
   */
  zoomTap?: number;
  /** فلش‌های رفته که کاشی می‌شوند؛ پیش‌فرض: هر فلشِ نازنده. همکاری فلش‌های تکه‌ی بسته را کاشی نمی‌کند. */
  cleared?: readonly boolean[];
  /** تخته تمام شد: دوربین کلِ نقش را نشان می‌دهد و موجی از مرکز روی کاشی‌ها می‌دود. */
  done?: boolean;
  className?: string;
}

interface Geom {
  d: string;
  /** درازای دیده‌شده: از مرکزِ دُم تا مرکزِ نوک. */
  len: number;
  rayLen: number;
  hx: number;
  hy: number;
  dx: number;
  dy: number;
  angle: number;
  /** کاشی کی بنشیند (میلی‌ثانیه از لمس): وقتی دُمِ فلشِ پرنده از خانه‌ی نوک گذشت. */
  lay: number;
}

interface Anim {
  kind: "fly" | "bump";
  t0: number;
  dur: number;
  /** fly: کلِ راه؛ bump: تا کجا جلو می‌رود. */
  reach: number;
}

/** دوربین: s پیکسل برای هر خانه؛ x و y جای گوشه‌ی تخته روی صفحه. */
interface View { s: number; x: number; y: number }

const ANGLE = [-90, 0, 90, 180];
/** بیشترین بزرگ‌نمایی: هر خانه این‌قدر پیکسل. */
const MAX_CELL = 96;
/** نقشه‌ی بزرگ (zoomTap): لمسِ دوردست دوربین را تا این‌قدر پیکسل برای هر خانه نزدیک می‌کند. */
const TAP_CELL = 30;
/** این‌قدر جابه‌جایی (پیکسل) یعنی کشیدن، نه لمس. */
const SLOP = 8;
/** پروازِ فلش بیرونِ تخته این‌قدر خانه ادامه دارد و در همین راه کم‌رنگ می‌شود. */
const TRAIL = 5;
/** این‌قدر فلشِ مانده (از سهمِ من) یعنی «فلش‌های آخر»: می‌تپند و دوربین سراغشان می‌رود. */
const FIND_LAST = 5;
/** تخته‌ی کوچک‌تر از این خودش پیداست؛ یابنده فقط از این‌قدر فلش به بالا. */
const FIND_FROM = 15;
/** دوربینِ یابنده از این بزرگ‌تر نمی‌کند (پیکسل برای هر خانه)، مگر جا شدنِ کلِ تخته خودش بزرگ‌تر باشد. */
const FIND_CELL = 44;
/** حلقه‌ی دورِ نوکِ فلش‌های آخر در هر بزرگ‌نمایی همین‌قدر پیکسل است (arrows.css: ‎.ar-ping). */
const PING_PX = 26;

/** پروازِ فلش: کلِ راه و زمانش — پرواز و زمانِ کاشی از همین. */
const flight = (dist: number, len: number) => {
  const reach = dist + len + TRAIL + 1;
  return { reach, dur: 200 + reach * 26 };
};

function geom(b: Board, cells: readonly number[], dir: 0 | 1 | 2 | 3, rayLen: number): Geom {
  const pts = cells.map((c) => [(c % b.w) + 0.5, Math.floor(c / b.w) + 0.5] as const);
  const [hx, hy] = pts[pts.length - 1]!;
  const dx = DX[dir], dy = DY[dir];
  const ext = rayLen + cells.length + TRAIL + 2;
  const d = "M" + pts.map(([x, y]) => `${x} ${y}`).join("L") + `L${hx + dx * ext} ${hy + dy * ext}`;
  const len = cells.length - 1;
  // دُمِ تکه‌ی دیده‌شده در راهِ t است (frame)؛ خانه‌ی نوک (len) وقتی خالی است که t از len + ۰٫۵ بگذرد.
  const { reach, dur } = flight(rayLen, len);
  const p = (-0.3 + Math.sqrt(0.09 + (2.8 * (len + 0.5)) / reach)) / 1.4;
  return { d, len, rayLen, hx, hy, dx, dy, angle: ANGLE[dir]!, lay: Math.round(p * dur) };
}

const ease = (p: number) => 1 - (1 - p) ** 3;

export const BoardView = forwardRef<BoardHandle, Props>(function BoardView(
  { board, alive, onTap, locked, interactive = true, inset, own, mates, focus, parts, follow, zoomTap, cleared, done, className }, ref) {
  const geoms = useMemo(() => board.arrows.map((a) => geom(board, a.cells, headDir(board, a), ray(board, a).length)), [board]);
  // کاشی‌های هر فلش، یک بار برای هر تخته: هر نوعِ کاشی یک مسیر و نقشِ وسطش مسیرِ دیگر، تا هزار فلش چند هزار گره نشوند.
  // همان عنصرها در هر رندر، تا React از رویشان رد شود.
  const tiles = useMemo(() => board.arrows.map((a) => {
    const base = ["", "", "", ""], motif = ["", "", "", ""];
    let r = 0;
    for (const c of a.cells) {
      const x = c % board.w, y = Math.floor(c / board.w), k = tileKind(x, y, board.w, board.h);
      base[k] += tilePath(x, y);
      motif[k] += motifPath(x, y);
      r += ringOf(x, y, board.w, board.h);
    }
    return (
      <g className="ar-gl" style={{ "--r": (r / a.cells.length).toFixed(2) } as React.CSSProperties}>
        {base.map((d, k) => d && <path key={k} d={d} fill={TILE_BASE[k]} />)}
        {motif.map((d, k) => d && <path key={k + 4} d={d} fill={TILE_MOTIF[k]} />)}
      </g>
    );
  }), [board]);

  // نقطه‌های پررنگ‌ترِ خانه‌هایی که از اول فلش داشتند: شکلِ تخته، و جای فلش‌های رفته — روی تخته‌ی مشترک به رنگِ صاحب.
  const dots = useMemo(() => board.arrows.flatMap((a) => a.cells.map((c) => [c, a.owner ?? -1] as const)), [board]);
  const grid = useId().replace(/:/g, "");
  const els = useRef(new Map<number, SVGGElement>());
  const anims = useRef(new Map<number, Anim>());
  const raf = useRef(0);
  const [flying, setFlying] = useState<ReadonlySet<number>>(() => new Set());
  const svg = useRef<SVGSVGElement>(null);
  const world = useRef<SVGGElement>(null);
  const view = useRef<View>({ s: 30, x: 0, y: 0 });
  const camAnim = useRef(0);
  /** کاربر خودش دوربین را جابه‌جا کرده؟ تا وقتی نکرده، با عوض‌شدنِ اندازه‌ی صفحه تخته دوباره جا می‌شود. */
  const moved = useRef(false);
  const top = inset?.top ?? 0, bottom = inset?.bottom ?? 0;

  // ---- دوربین ----

  const pings = useRef<SVGGElement | null>(null);
  const apply = useCallback(() => {
    const v = view.current;
    world.current?.setAttribute("transform", `translate(${v.x} ${v.y}) scale(${v.s})`);
    // فقط گروهِ حلقه‌ها: متغیرِ روی کلِ svg هزاران گره را در هر فریم از نو سبک می‌کرد
    pings.current?.style.setProperty("--s", v.s.toFixed(3));
  }, []);

  const box = () => {
    const r = svg.current?.getBoundingClientRect();
    return { w: r?.width ?? 0, h: r?.height ?? 0, left: r?.left ?? 0, top: r?.top ?? 0 };
  };

  /** دوربینی که چهارگوشِ r (پیش‌فرض: کلِ تخته) را در صفحه جا کند. */
  const fitBox = useCallback((r = { x: 0, y: 0, w: board.w, h: board.h }): View => {
    const { w, h } = box();
    if (w <= 0 || h <= 0) return view.current; // هنوز اندازه ندارد؛ ResizeObserver بعداً جا می‌کند
    const usable = Math.max(80, h - top - bottom);
    const s = Math.min(MAX_CELL, w / (r.w + 1.2), usable / (r.h + 1.2));
    return { s, x: (w - r.w * s) / 2 - r.x * s, y: top + (usable - r.h * s) / 2 - r.y * s };
  }, [board, top, bottom]);
  // «جا کن» و چرخشِ صفحه: همکاری به تکه‌های باز (follow)، وگرنه به سهمِ خودم (focus)
  const fitView = useCallback(() => fitBox(follow ?? focus), [fitBox, follow, focus]);

  const limits = useCallback(() => {
    const f = fitBox().s;
    return { min: f * 0.5, max: Math.max(fitView().s * 4, MAX_CELL) };
  }, [fitBox, fitView]);

  /** تخته هر جا برود، دست‌کم تکه‌ای از آن در صفحه می‌ماند. */
  const clamp = useCallback((v: View): View => {
    const { w, h } = box();
    const keep = 56;
    const bw = board.w * v.s, bh = board.h * v.s;
    return {
      s: v.s,
      x: Math.min(w - keep, Math.max(keep - bw, v.x)),
      y: Math.min(h - keep, Math.max(keep - bh, v.y)),
    };
  }, [board]);

  const set = useCallback((v: View) => {
    cancelAnimationFrame(camAnim.current);
    view.current = clamp(v);
    apply();
  }, [apply, clamp]);

  const glide = useCallback((to: View, ms = 320) => {
    cancelAnimationFrame(camAnim.current);
    const from = { ...view.current }, t0 = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - t0) / ms), k = ease(p);
      view.current = { s: from.s + (to.s - from.s) * k, x: from.x + (to.x - from.x) * k, y: from.y + (to.y - from.y) * k };
      apply();
      if (p < 1) camAnim.current = requestAnimationFrame(step);
    };
    camAnim.current = requestAnimationFrame(step);
  }, [apply]);

  /** بزرگ‌نمایی با ضریبِ f گرداگردِ نقطه‌ی (px, py) ِ صفحه، که زیرِ انگشت ثابت می‌ماند. */
  const zoomAt = useCallback((f: number, px: number, py: number, from: View = view.current): View => {
    const { min, max } = limits();
    const s = Math.min(max, Math.max(min, from.s * f));
    const k = s / from.s;
    return { s, x: px - (px - from.x) * k, y: py - (py - from.y) * k };
  }, [limits]);

  // آغاز: کمی کوچک‌تر، بعد آرام تا اندازه‌ی جا — مثلِ دوربینی که به تخته‌ی تازه می‌رسد.
  // با focus (تخته‌ی مشترک): اول کلِ شکل، بعد دوربین آرام روی سهمِ خودم.
  // (به fitView بسته نیست: باز شدنِ تکه‌ی تازه‌ی همکاری این آغاز را از نو نمی‌دواند.)
  useLayoutEffect(() => {
    const all = fitBox(), f = fitBox(focus);
    const { w, h } = box();
    view.current = interactive ? zoomAtStatic(all, 0.86, w / 2, top + (h - top - bottom) / 2) : all;
    apply();
    moved.current = false;
    if (!interactive) return;
    glide(all, 420);
    if (!focus) return;
    const t = setTimeout(() => { if (!moved.current) glide(f, 650); }, 900);
    return () => clearTimeout(t);
  }, [board, fitBox, apply, glide, interactive, top, bottom, focus]);

  // همکاری: تکه‌ی تازه که باز شد، دوربین سراغِ همه‌ی تکه‌های باز می‌رود — حتی اگر بازیکن جابه‌جایش کرده بود.
  const followKey = follow ? `${follow.x},${follow.y},${follow.w},${follow.h}` : "";
  const followed = useRef(followKey);
  useEffect(() => {
    if (!follow || followKey === followed.current) return;
    followed.current = followKey;
    moved.current = false;
    glide(fitBox(follow), 750);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followKey, fitBox, glide]);

  // فقط عوض‌شدنِ واقعیِ اندازه (چرخشِ گوشی، پنجره) دوباره جا می‌کند؛ صدای اولِ ResizeObserver هنگامِ
  // سوارشدن، حرکتِ آغازِ دوربین را نمی‌بُرد.
  useEffect(() => {
    const el = svg.current;
    if (!el) return;
    let last = `${Math.round(el.clientWidth)}x${Math.round(el.clientHeight)}`;
    const ro = new ResizeObserver(() => {
      const now = `${Math.round(el.clientWidth)}x${Math.round(el.clientHeight)}`;
      if (now === last) return;
      last = now;
      if (!moved.current) set(fitView());
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fitView, set]);

  // ---- انیمیشنِ فلش‌ها ----

  const place = useCallback((id: number, t: number, fade: number) => {
    const g = els.current.get(id);
    const m = geoms[id];
    if (!g || !m) return;
    const [line, head] = Array.from(g.children) as SVGElement[];
    line!.style.strokeDashoffset = String(-t);
    head!.setAttribute("transform", `translate(${m.hx + m.dx * t} ${m.hy + m.dy * t}) rotate(${m.angle})`);
    g.style.opacity = String(fade);
  }, [geoms]);

  const frame = useCallback(function frame(now: number) {
    const done: number[] = [];
    for (const [id, an] of anims.current) {
      const p = Math.min(1, (now - an.t0) / an.dur);
      const m = geoms[id]!;
      if (an.kind === "fly") {
        // تند می‌شود، مثلِ چیزی که رها شده؛ بیرونِ تخته کم‌رنگ می‌شود.
        const t = an.reach * (0.3 * p + 0.7 * p * p);
        place(id, t, 1 - Math.min(1, Math.max(0, (t - m.rayLen - m.len) / TRAIL)));
      } else {
        place(id, an.reach * Math.sin(Math.PI * p), 1);
      }
      if (p >= 1) done.push(id);
    }
    for (const id of done) {
      const an = anims.current.get(id)!;
      anims.current.delete(id);
      if (an.kind === "fly") setFlying((s) => { const n = new Set(s); n.delete(id); return n; });
      else place(id, 0, 1);
    }
    raf.current = anims.current.size ? requestAnimationFrame(frame) : 0;
  }, [geoms, place]);

  const start = useCallback((id: number, an: Anim) => {
    anims.current.set(id, an);
    if (!raf.current) raf.current = requestAnimationFrame(frame);
  }, [frame]);



  useEffect(() => () => {
    cancelAnimationFrame(raf.current);
    cancelAnimationFrame(camAnim.current);
    raf.current = 0;
  }, []);

  useImperativeHandle(ref, () => ({
    fly(id, dist) {
      const m = geoms[id];
      if (!m) return;
      const { reach, dur } = flight(dist, m.len);
      setFlying((s) => new Set(s).add(id));
      // در پرواز بالای بقیه، با سایه — دو فلشِ پرنده از روی هم رد می‌شوند و کاری به هم ندارند.
      const g = els.current.get(id);
      g?.classList.add("fly");
      g?.parentNode?.appendChild(g);
      start(id, { kind: "fly", t0: performance.now(), dur, reach });
    },
    bump(id, dist) {
      const reach = Math.max(0.22, dist - 1 + 0.22);
      const g = els.current.get(id);
      g?.classList.add("bad");
      setTimeout(() => g?.classList.remove("bad"), 520);
      start(id, { kind: "bump", t0: performance.now(), dur: Math.min(560, 170 + reach * 70), reach });
    },
    zoomBy(f) {
      const { w, h } = box();
      moved.current = true;
      glide(clamp(zoomAt(f, w / 2, top + (h - top - bottom) / 2)), 220);
    },
    fit() {
      moved.current = false;
      glide(fitView());
    },
  }), [geoms, start, glide, clamp, zoomAt, fitView, top, bottom]);

  // ---- فلش‌های آخر و پایان ----

  // سهمِ من: فلش‌هایی که خودم باید بزنم (تخته‌ی مشترک: رنگِ خودم؛ تک‌نفره و همکاری: همه) — فقط آن‌ها که در بازی‌اند:
  // فلشِ تکه‌ی بستهِ همکاری نه زنده است نه رفته، و نباید «پنج فلشِ آخر» را از آغاز بسازد.
  const gone = cleared ?? alive.map((v) => !v);
  const mine = useMemo(() => board.arrows.filter((a) => own === undefined || a.owner === own).map((a) => a.id), [board, own]);
  const inPlay = mine.filter((id) => alive[id] || gone[id]);
  const lastIds = interactive && inPlay.length >= FIND_FROM ? inPlay.filter((id) => alive[id]) : [];
  const last = lastIds.length > 0 && lastIds.length <= FIND_LAST ? lastIds : null;
  const found = useRef(false);
  useEffect(() => { found.current = false; }, [board, own]);
  useEffect(() => {
    if (!last || found.current) return;
    found.current = true;
    let x0 = board.w, y0 = board.h, x1 = 0, y1 = 0;
    for (const id of last) for (const c of board.arrows[id]!.cells) {
      const x = c % board.w, y = Math.floor(c / board.w);
      x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x + 1); y1 = Math.max(y1, y + 1);
    }
    const v = view.current, { w, h } = box();
    const seen = v.s >= 18 && x0 * v.s + v.x >= 0 && x1 * v.s + v.x <= w && y0 * v.s + v.y >= top && y1 * v.s + v.y <= h - bottom;
    if (seen) return;
    const r = { x: x0 - 1.5, y: y0 - 1.5, w: x1 - x0 + 3, h: y1 - y0 + 3 };
    let to = fitBox(r);
    const cap = Math.max(fitView().s, FIND_CELL);
    if (to.s > cap) {
      const usable = Math.max(80, h - top - bottom);
      to = { s: cap, x: w / 2 - (r.x + r.w / 2) * cap, y: top + usable / 2 - (r.y + r.h / 2) * cap };
    }
    moved.current = true;
    glide(clamp(to), 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [last !== null, board]);

  // پایان: کلِ نقشِ کاشی‌شده در صفحه.
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => { moved.current = false; glide(fitBox(), 650); }, 250);
    return () => clearTimeout(t);
  }, [done, fitBox, glide]);

  // ---- لمس، کشیدن و بزرگ‌نمایی ----

  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<
    | { kind: "press"; x0: number; y0: number; v0: View; arrow: number }
    | { kind: "pan"; x0: number; y0: number; v0: View }
    | { kind: "pinch"; d0: number; mx: number; my: number; v0: View }
    | null
  >(null);

  const press = (id: number, on: boolean) => els.current.get(id)?.classList.toggle("press", on);

  const arrowAt = (cx: number, cy: number): number => {
    const { left, top: t } = box();
    const v = view.current;
    return pick(board, alive, (cx - left - v.x) / v.s, (cy - t - v.y) / v.s, own);
  };

  const pinchFrom = () => {
    const [a, b] = [...pointers.current.values()];
    const { left, top: t } = box();
    return { d0: Math.hypot(a!.x - b!.x, a!.y - b!.y) || 1, mx: (a!.x + b!.x) / 2 - left, my: (a!.y + b!.y) / 2 - t };
  };

  const down = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!interactive) return;
    try { svg.current?.setPointerCapture(e.pointerId); } catch { /* اشاره‌گرِ ازدست‌رفته: بی گرفتن هم کار می‌کند */ }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    cancelAnimationFrame(camAnim.current);
    const g = gesture.current;
    if (g?.kind === "press") press(g.arrow, false);
    if (pointers.current.size === 2) {
      gesture.current = { kind: "pinch", ...pinchFrom(), v0: { ...view.current } };
    } else if (pointers.current.size === 1) {
      const far = zoomTap !== undefined && view.current.s < zoomTap;
      const arrow = far ? -1 : arrowAt(e.clientX, e.clientY);
      if (arrow >= 0 && !locked) press(arrow, true);
      gesture.current = { kind: "press", x0: e.clientX, y0: e.clientY, v0: { ...view.current }, arrow };
    }
  };

  const move = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    if (g.kind === "pinch" && pointers.current.size >= 2) {
      const now = pinchFrom();
      const z = zoomAt(now.d0 / g.d0, g.mx, g.my, g.v0);
      set({ s: z.s, x: z.x + (now.mx - g.mx), y: z.y + (now.my - g.my) });
      moved.current = true;
      return;
    }
    if (g.kind === "press" && Math.hypot(e.clientX - g.x0, e.clientY - g.y0) > SLOP) {
      press(g.arrow, false);
      gesture.current = { kind: "pan", x0: g.x0, y0: g.y0, v0: g.v0 };
    }
    const p = gesture.current;
    if (p?.kind === "pan") {
      set({ s: p.v0.s, x: p.v0.x + e.clientX - p.x0, y: p.v0.y + e.clientY - p.y0 });
      moved.current = true;
    }
  };

  const up = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!pointers.current.delete(e.pointerId)) return;
    const g = gesture.current;
    if (g?.kind === "press") {
      press(g.arrow, false);
      gesture.current = null;
      if (e.type === "pointerup" && zoomTap !== undefined && view.current.s < zoomTap) {
        const { left, top: t } = box();
        moved.current = true;
        glide(clamp(zoomAt(TAP_CELL / view.current.s, e.clientX - left, e.clientY - t)), 380);
        return;
      }
      if (e.type === "pointerup" && g.arrow >= 0 && !locked && onTap) onTap(g.arrow);
      return;
    }
    if (g?.kind === "pinch" && pointers.current.size === 1) {
      // یک انگشت ماند: از همین‌جا کشیدن ادامه دارد.
      const [p] = [...pointers.current.values()];
      gesture.current = { kind: "pan", x0: p!.x, y0: p!.y, v0: { ...view.current } };
      return;
    }
    if (!pointers.current.size) gesture.current = null;
  };

  // چرخِ موش و نیشگونِ ترک‌پد (ctrl + چرخ): passive:false تا صفحه خودش اسکرول نشود.
  useEffect(() => {
    const el = svg.current;
    if (!el || !interactive) return;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      const { left, top: t } = box();
      const f = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0018));
      moved.current = true;
      set(zoomAt(f, e.clientX - left, e.clientY - t));
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, [interactive, set, zoomAt]);

  return (
    <svg ref={svg} className={"ar-board" + (done ? " done" : "") + (className ? " " + className : "")}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      role="application" aria-label="تخته‌ی فلش‌ها">
      <defs>
        <pattern id={grid} width={1} height={1} patternUnits="userSpaceOnUse">
          <circle cx={0.5} cy={0.5} r={0.055} className="ar-dot" />
        </pattern>
      </defs>
      <g ref={world}>
        <rect x={-400} y={-400} width={800 + board.w} height={800 + board.h} fill={`url(#${grid})`} />
        {board.hub && <Hub {...board.hub} />}
        {dots.map(([c, o]) => <circle key={c} className="ar-cell" cx={(c % board.w) + 0.5} cy={Math.floor(c / board.w) + 0.5} r={0.085}
          style={o >= 0 ? { fill: PLAYER_COLORS[o], opacity: 0.35 } : undefined} />)}
        <g className="ar-tiles" aria-hidden>
          {board.arrows.map((a) => gone[a.id] ? (
            <g key={a.id} className="ar-tl" style={{ "--lay": `${geoms[a.id]!.lay}ms` } as React.CSSProperties}>
              {tiles[a.id]}
            </g>
          ) : null)}
        </g>
        {parts?.map((p, i) => (
          <g key={i} className={`ar-part ${p.state}`} aria-hidden>
            <rect x={p.x - 0.35} y={p.y - 0.35} width={p.w + 0.7} height={p.h + 0.7} rx={0.9} />
            {p.state === "locked" && (
              <g transform={`translate(${p.x + p.w / 2} ${p.y + p.h / 2}) scale(${Math.max(1, Math.min(p.w, p.h) / 9)})`}>
                <rect x={-0.95} y={-0.15} width={1.9} height={1.45} rx={0.35} className="lock" />
                <path d="M-0.55 -0.15V-0.6a0.55 0.55 0 0 1 1.1 0V-0.15" className="shackle" />
              </g>
            )}
            {p.state === "done" && (
              <path d={`M${p.x + p.w / 2 - 0.9} ${p.y + p.h / 2}l0.65 0.65l1.25 -1.3`} className="tick" />
            )}
          </g>
        ))}
        {board.arrows.map((a) => {
          if (!alive[a.id] && !flying.has(a.id)) return null;
          const m = geoms[a.id]!;
          const color = a.owner !== undefined ? PLAYER_COLORS[a.owner] : undefined;
          const side = own === undefined || a.owner === undefined ? ""
            : a.owner === own ? " mine" : mates?.includes(a.owner) ? " mate" : " other";
          return (
            <g key={a.id} className={"ar" + side + (last?.includes(a.id) ? " last" : "")} style={color ? { "--c": color, "--cf": color } as React.CSSProperties : undefined}
              ref={(el) => { if (el) els.current.set(a.id, el); else els.current.delete(a.id); }}>
              <path className="line" d={m.d} strokeDasharray={`${m.len} 999`} />
              <path className="head" d="M0.34 0L-0.1 -0.25L-0.1 0.25Z" transform={`translate(${m.hx} ${m.hy}) rotate(${m.angle})`} />
            </g>
          );
        })}
        {last && (
          <g className="ar-pings" aria-hidden style={{ "--px": PING_PX } as React.CSSProperties} ref={(el) => { pings.current = el; el?.style.setProperty("--s", view.current.s.toFixed(3)); }}>
            {last.map((id) => (
              <g key={id} transform={`translate(${geoms[id]!.hx} ${geoms[id]!.hy})`}>
                <g className="ar-ping"><circle r={1} /></g>
              </g>
            ))}
          </g>
        )}
      </g>
    </svg>
  );
});

/**
 * نقشِ وسطِ تخته‌ی مشترک، جایی که هیچ فلشی نیست: شمسه (ستاره‌ی هشت‌پر در دایره)، ترنجِ فرش (لوزیِ
 * تو در تو)، یا تنِ پروانه با دو شاخک.
 */
function Hub({ x, y, w, h, kind }: NonNullable<Board["hub"]>) {
  const cx = x + w / 2, cy = y + h / 2;
  if (kind === "toranj") {
    const lozenge = (k: number) => `${cx},${cy - (h / 2) * k} ${cx + (w / 2) * k},${cy} ${cx},${cy + (h / 2) * k} ${cx - (w / 2) * k},${cy}`;
    return (
      <g className="ar-hub" aria-hidden>
        <polygon points={lozenge(0.96)} className="ring" />
        <polygon points={lozenge(0.66)} className="star" />
        <polygon points={lozenge(0.2)} className="core" />
      </g>
    );
  }
  if (kind === "body") {
    const r = w * 0.28, top = y + 0.3;
    return (
      <g className="ar-hub" aria-hidden>
        <path d={`M${cx - r * 0.5} ${top + r} q${-r * 2} ${-r * 3} ${-r * 4} ${-r * 4.5}M${cx + r * 0.5} ${top + r} q${r * 2} ${-r * 3} ${r * 4} ${-r * 4.5}`}
          className="feeler" />
        <circle cx={cx} cy={top + r} r={r * 1.15} className="core" />
        <rect x={cx - r} y={top + r * 2.2} width={r * 2} height={h - r * 2.6} rx={r} className="core" />
      </g>
    );
  }
  const size = Math.min(w, h), R = size * 0.46, r = R * 0.62;
  const pts = Array.from({ length: 16 }, (_, k) => {
    const a = (Math.PI / 8) * k - Math.PI / 2, d = k % 2 ? r : R;
    return `${cx + d * Math.cos(a)},${cy + d * Math.sin(a)}`;
  }).join(" ");
  return (
    <g className="ar-hub" aria-hidden>
      <circle cx={cx} cy={cy} r={R * 1.04} className="ring" />
      <polygon points={pts} className="star" />
      <circle cx={cx} cy={cy} r={r * 0.42} className="core" />
    </g>
  );
}

function zoomAtStatic(v: View, f: number, px: number, py: number): View {
  return { s: v.s * f, x: px - (px - v.x) * f, y: py - (py - v.y) * f };
}

/**
 * فلشِ زیرِ انگشت: خانه‌ی خودِ نقطه، وگرنه نزدیک‌ترین خانه‌ی پُر تا ۰٫۸ خانه — انگشت از خانه‌ی کوچک
 * بزرگ‌تر است و لمسِ لبِ فلش نباید هدر برود. x و y به واحدِ خانه‌اند. own: فقط فلش‌های این صاحب
 * (تخته‌ی مشترک). −۱ = هیچ.
 */
export function pick(b: Board, alive: readonly boolean[], x: number, y: number, own?: number): number {
  const occ = occupancy(b, own === undefined ? alive : alive.map((v, i) => v && b.arrows[i]!.owner === own));
  const cx = Math.floor(x), cy = Math.floor(y);
  if (cx >= 0 && cy >= 0 && cx < b.w && cy < b.h && occ[cy * b.w + cx]! >= 0) return occ[cy * b.w + cx]!;
  let best = -1, bestD = 0.8 * 0.8;
  for (let c = 0; c < occ.length; c++) {
    if (occ[c]! < 0) continue;
    const ddx = (c % b.w) + 0.5 - x, ddy = Math.floor(c / b.w) + 0.5 - y;
    const d = ddx * ddx + ddy * ddy;
    if (d < bestD) { bestD = d; best = occ[c]!; }
  }
  return best;
}
